import {
  ActionListItem,
  GhostIconButton,
  Icon,
  Loader,
  downloadPdfFromResponse,
  showToast,
} from "@certn/cascadia-react";
import { useContract } from "@certn/cascadia-react/miscellaneous/contract/hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConsentModal } from "./ConsentModal";
import { ConsentListItem, isSuperDocConsent } from "./consentListItem";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";
import { CLICKWRAP_ACCESS_ID } from "../constants";

interface Props {
  consent: ConsentListItem;
}

export function ConsentActionListItem(props: Props) {
  const { enableSuperdocApplicantAgreementTemplates } = useFlags();
  if (
    isSuperDocConsent(props.consent, enableSuperdocApplicantAgreementTemplates)
  ) {
    return <SuperDocConsentActionListItem consent={props.consent} />;
  }
  return <PactSafeConsentActionListItem consent={props.consent} />;
}

function getConsentDownloadTestId(consent: ConsentListItem) {
  return `${consent.id || consent.external_id}-download-button`;
}

function PactSafeConsentActionListItem({ consent }: Props) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();

  const { renderData, contractIDs } = useMemo(
    () => ({
      renderData: consent.render_data,
      contractIDs: [consent.external_id],
    }),
    [consent.external_id, consent.render_data]
  );

  const { data: contractsData, isLoading } = useContract({
    accessID: CLICKWRAP_ACCESS_ID,
    contractIDs,
    signerID: caseId,
    renderData,
  });

  if (isLoading) {
    return <Loader height={48} />;
  }

  return (
    <ActionListItem
      title={<ConsentModal consent={consent} />}
      alignItems="center"
      leading={<Icon name="description" />}
      trailing={
        <GhostIconButton
          icon="download"
          testID={getConsentDownloadTestId(consent)}
          aria-label={t("Download")}
          onPress={() =>
            open(
              contractsData?.contract_data[consent.external_id]?.download_url,
              "_blank"
            )
          }
          target="_blank"
        />
      }
    />
  );
}

function SuperDocConsentActionListItem({ consent }: Props) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await createESignDownloadHandler(caseId, consent.id)();
      await downloadPdfFromResponse(response, consent.template_name);
    } catch (e) {
      console.error("Error downloading SuperDoc consent", e);
      showToast(t("Failed to download document"), { type: "error" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ActionListItem
      title={<ConsentModal consent={consent} />}
      alignItems="center"
      leading={<Icon name="description" />}
      trailing={
        <GhostIconButton
          icon="download"
          testID={getConsentDownloadTestId(consent)}
          aria-label={t("Download")}
          isLoading={isDownloading}
          onPress={handleDownload}
        />
      }
    />
  );
}
